import apiClient from './apiClient';

// ---- Course tree shapes (as served by GET /api/v1/courses/:courseId) --------
// Mirrors the nesting Week -> Module -> Lesson that the LeftNav renders as a
// mail-client folder tree. Kept permissive; the backend owns the canonical shape.

export type LessonStatus = 'not_started' | 'in_progress' | 'completed' | 'locked';

export interface Lesson {
  id: string;
  index: number;
  title: string;
  status?: LessonStatus;
}

export interface Module {
  id: string;
  index: number;
  title: string;
  lessons: Lesson[];
}

// A scheduled session within a week (the D2L calendar plan). `type` drives the
// chip styling on the Dashboard; `lesson`/`part` link a lecture session to its
// content. Kept permissive — the backend owns the canonical set.
export type SessionType =
  | 'intro'
  | 'lecture'
  | 'review'
  | 'test'
  | 'reading'
  | 'activity'
  | string;

export interface WeekSession {
  type: SessionType;
  label: string;
  lesson?: number;
  part?: string;
}

export interface WeekLectureRef {
  lesson: number;
  part?: string;
}

export interface WeekSchedule {
  sessions: WeekSession[];
  due: string[];
  lectures: WeekLectureRef[];
}

export interface Week {
  id: string;
  index: number;
  title: string;
  summary?: string | null;
  image_url?: string | null;
  schedule?: WeekSchedule | null;
  modules: Module[];
}

export interface CourseSummary {
  id: string;
  code?: string;
  title: string;
  description?: string;
}

export interface CourseDetail {
  course: CourseSummary;
  weeks: Week[];
}

// All courses the user is enrolled in (Dashboard course picker).
export async function listCourses(): Promise<CourseSummary[]> {
  const { data } = await apiClient.get<CourseSummary[]>('/api/v1/courses');
  return data;
}

// Full week/module/lesson tree for one course.
export async function getCourse(courseId: string): Promise<CourseDetail> {
  const { data } = await apiClient.get<CourseDetail>(`/api/v1/courses/${courseId}`);
  return data;
}
